import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Dialog, Field, Input, Section, Select, Spinner, Textarea } from '@zt/ui';
import { ConfirmButton } from './Confirm';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useT } from '../lib/i18n';
import { languageName, storeLanguages } from '../lib/languages';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { useStoreChoice } from '../lib/store-choice';
import type { CategoryTranslations, ProductTranslations } from '../lib/types';

/**
 * Ürün ve kategori çevirileri (F7-14, ADR-119): mağazaların pazar dillerinde (ana dil hariç) ad, açıklama ve SEO
 * alanları. Çevirisi olan sayfa o dilin önekli adresinde dizine açılır ve hreflang'de yer alır. Sağlık içerikli
 * üründe çeviri Veteriner Uzman İnceleyici onayıyla yayınlanır.
 */

/** İşletmenin tüm mağazalarındaki diller (ana dil hariç). */
function useTargetLanguages(baseLocale: string | undefined): string[] {
  const { stores } = useStoreChoice();
  const all = [...new Set(stores.flatMap((s) => storeLanguages(s)))];
  return all.filter((l) => l !== (baseLocale ?? 'tr'));
}

type ProductRow = ProductTranslations['items'][number];

const vetTone = { approved: 'moss', pending: 'amber', not_required: 'slate' } as const;

export function ProductTranslationsSection({ productId }: { productId: string }) {
  const t = useT();
  const { can } = usePermissions();
  const canWrite = can('catalog.product.write');
  const canApprove = can('catalog.health_claim.approve');
  const q = useQuery({
    queryKey: ['product-translations', productId],
    queryFn: () => api.get<ProductTranslations>(`/v1/admin/products/${productId}/translations`),
  });
  const langs = useTargetLanguages(q.data?.baseLocale);
  const [edit, setEdit] = useState<{ locale: string; row: ProductRow | null } | null>(null);
  const del = useApiMutation(
    (locale: string) => api.del(`/v1/admin/products/${productId}/translations/${locale}`),
    { invalidate: [['product-translations', productId]], success: t('common.deleted') },
  );
  const approve = useApiMutation(
    (r: ProductRow) =>
      api.post(`/v1/admin/products/${productId}/translations/${r.locale}/approve`, {
        contentHash: r.contentHash,
      }),
    { invalidate: [['product-translations', productId]], success: t('admin.catalogTranslations.approved') },
  );
  if (!q.data) return null;
  if (!langs.length) return null;
  return (
    <Section
      title={t('admin.catalogTranslations.title')}
      description={t('admin.catalogTranslations.productLead', { base: languageName(q.data.baseLocale) })}
      action={<HelpTip k="products.translations" />}
    >
      <ul
        className="divide-y divide-line rounded-md border border-line"
        aria-label={t('admin.catalogTranslations.title')}
      >
        {langs.map((l) => {
          const r = q.data.items.find((x) => x.locale === l) ?? null;
          return (
            <li key={l} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
              <div className="min-w-[14rem] flex-1">
                <p className="font-medium">{languageName(l)}</p>
                <p className="text-sm text-ink-2">{r ? r.title : t('admin.catalogTranslations.missing')}</p>
                {r && r.findings.length > 0 && (
                  <p className="text-sm text-amber">
                    {t('admin.catalogTranslations.findings', { count: r.findings.length })}
                  </p>
                )}
              </div>
              {r && r.vetStatus !== 'not_required' && (
                <Badge tone={vetTone[r.vetStatus]}>{t(`admin.catalogTranslations.vet.${r.vetStatus}`)}</Badge>
              )}
              {r && canApprove && r.vetStatus === 'pending' && (
                <ConfirmButton
                  variant="secondary"
                  message={t('admin.catalogTranslations.approveConfirm', { language: languageName(l) })}
                  onConfirm={() => approve.mutateAsync(r)}
                >
                  {t('admin.catalogTranslations.approve')}
                </ConfirmButton>
              )}
              {canWrite && (
                <Button size="sm" variant="ghost" onClick={() => setEdit({ locale: l, row: r })}>
                  {r ? t('common.edit') : t('admin.catalogTranslations.add')}
                </Button>
              )}
              {canWrite && r && (
                <ConfirmButton
                  message={t('admin.catalogTranslations.deleteConfirm', { language: languageName(l) })}
                  onConfirm={() => del.mutateAsync(l)}
                >
                  {t('common.delete')}
                </ConfirmButton>
              )}
            </li>
          );
        })}
      </ul>
      {edit && (
        <ProductTranslationDialog
          productId={productId}
          locale={edit.locale}
          row={edit.row}
          onClose={() => setEdit(null)}
        />
      )}
    </Section>
  );
}

function ProductTranslationDialog({
  productId,
  locale,
  row,
  onClose,
}: {
  productId: string;
  locale: string;
  row: ProductRow | null;
  onClose: () => void;
}) {
  const t = useT();
  const [f, setF] = useState({
    title: row?.title ?? '',
    shortDescription: row?.shortDescription ?? '',
    description: row?.description ?? '',
    seoTitle: row?.seoTitle ?? '',
    seoDescription: row?.seoDescription ?? '',
  });
  const save = useApiMutation(
    () =>
      api.put(
        `/v1/admin/products/${productId}/translations`,
        {
          locale,
          title: f.title,
          shortDescription: f.shortDescription.trim() || null,
          description: f.description.trim() || null,
          seoTitle: f.seoTitle.trim() || null,
          seoDescription: f.seoDescription.trim() || null,
        },
        row ? { ifMatch: row.version } : undefined,
      ),
    {
      invalidate: [['product-translations', productId]],
      success: t('common.saved'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const fe = (k: string) => fieldError(save.error, k);
  return (
    <Dialog
      open
      variant="sheet"
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.catalogTranslations.dialogTitle', { language: languageName(locale) })}
      description={t('admin.catalogTranslations.dialogLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={f.title.trim().length < 2}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4" lang={locale} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
        <Field label={t('admin.catalogTranslations.productTitle')} required error={fe('title')}>
          {(p) => (
            <Input
              {...p}
              maxLength={200}
              value={f.title}
              onChange={(e) => setF({ ...f, title: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.catalogTranslations.shortDescription')}
          optionalLabel={t('common.optional')}
          error={fe('shortDescription')}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              maxLength={500}
              value={f.shortDescription}
              onChange={(e) => setF({ ...f, shortDescription: e.target.value })}
            />
          )}
        </Field>
        <Field
          label={t('admin.catalogTranslations.description')}
          optionalLabel={t('common.optional')}
          error={fe('description')}
        >
          {(p) => (
            <Textarea
              {...p}
              rows={8}
              value={f.description}
              onChange={(e) => setF({ ...f, description: e.target.value })}
            />
          )}
        </Field>
        <SeoFields
          seoTitle={f.seoTitle}
          seoDescription={f.seoDescription}
          onChange={(v) => setF({ ...f, ...v })}
          error={fe}
        />
      </div>
    </Dialog>
  );
}

function SeoFields({
  seoTitle,
  seoDescription,
  onChange,
  error,
}: {
  seoTitle: string;
  seoDescription: string;
  onChange: (v: { seoTitle?: string; seoDescription?: string }) => void;
  error: (k: string) => string | undefined;
}) {
  const t = useT();
  return (
    <>
      <Field
        label={t('admin.catalogTranslations.seoTitle')}
        optionalLabel={t('common.optional')}
        help={<HelpTip k="translations.seo" />}
        description={t('admin.pages.chars', { count: seoTitle.length, max: 70 })}
        error={error('seoTitle')}
      >
        {(p) => (
          <Input
            {...p}
            maxLength={70}
            value={seoTitle}
            onChange={(e) => onChange({ seoTitle: e.target.value })}
          />
        )}
      </Field>
      <Field
        label={t('admin.catalogTranslations.seoDescription')}
        optionalLabel={t('common.optional')}
        description={t('admin.pages.chars', { count: seoDescription.length, max: 170 })}
        error={error('seoDescription')}
      >
        {(p) => (
          <Textarea
            {...p}
            rows={2}
            maxLength={170}
            value={seoDescription}
            onChange={(e) => onChange({ seoDescription: e.target.value })}
          />
        )}
      </Field>
    </>
  );
}

/** Kategori çevirileri: dil seçilir, alanlar doldurulur; kayıtlı çeviri silinebilir. */
export function CategoryTranslationsDialog({
  category,
  onClose,
}: {
  category: { id: string; name: string };
  onClose: () => void;
}) {
  const t = useT();
  const q = useQuery({
    queryKey: ['category-translations', category.id],
    queryFn: () => api.get<CategoryTranslations>(`/v1/admin/categories/${category.id}/translations`),
  });
  const langs = useTargetLanguages(q.data?.baseLocale);
  const [lang, setLang] = useState('');
  const locale = lang || langs[0] || '';
  const row = q.data?.items.find((x) => x.locale === locale) ?? null;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.catalogTranslations.categoryTitle', { name: category.name })}
      description={t('admin.catalogTranslations.categoryLead')}
      closeLabel={t('common.close')}
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !langs.length ? (
        <p className="text-sm text-ink-2">{t('admin.catalogTranslations.noLanguages')}</p>
      ) : (
        <div className="space-y-4">
          <Field label={t('admin.catalogTranslations.language')}>
            {(p) => (
              <Select {...p} value={locale} onChange={(e) => setLang(e.target.value)}>
                {langs.map((l) => (
                  <option key={l} value={l}>
                    {languageName(l)}
                    {q.data.items.some((x) => x.locale === l)
                      ? ` — ${t('admin.catalogTranslations.done')}`
                      : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <CategoryTranslationForm
            key={`${locale}-${row?.version ?? 0}`}
            categoryId={category.id}
            locale={locale}
            row={row}
          />
        </div>
      )}
    </Dialog>
  );
}

function CategoryTranslationForm({
  categoryId,
  locale,
  row,
}: {
  categoryId: string;
  locale: string;
  row: CategoryTranslations['items'][number] | null;
}) {
  const t = useT();
  const [f, setF] = useState({
    name: row?.name ?? '',
    description: row?.description ?? '',
    seoTitle: row?.seoTitle ?? '',
    seoDescription: row?.seoDescription ?? '',
  });
  const save = useApiMutation(
    () =>
      api.put(
        `/v1/admin/categories/${categoryId}/translations`,
        {
          locale,
          name: f.name,
          description: f.description.trim() || null,
          seoTitle: f.seoTitle.trim() || null,
          seoDescription: f.seoDescription.trim() || null,
        },
        row ? { ifMatch: row.version } : undefined,
      ),
    {
      invalidate: [['category-translations', categoryId], ['categories']],
      success: t('common.saved'),
      silentFieldErrors: true,
    },
  );
  const del = useApiMutation(() => api.del(`/v1/admin/categories/${categoryId}/translations/${locale}`), {
    invalidate: [['category-translations', categoryId], ['categories']],
    success: t('common.deleted'),
  });
  const fe = (k: string) => fieldError(save.error, k);
  return (
    <div className="space-y-4" lang={locale} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <Field label={t('admin.catalogTranslations.categoryName')} required error={fe('name')}>
        {(p) => (
          <Input
            {...p}
            maxLength={80}
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        )}
      </Field>
      <Field
        label={t('admin.catalogTranslations.description')}
        optionalLabel={t('common.optional')}
        error={fe('description')}
      >
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            maxLength={2000}
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        )}
      </Field>
      <SeoFields
        seoTitle={f.seoTitle}
        seoDescription={f.seoDescription}
        onChange={(v) => setF({ ...f, ...v })}
        error={fe}
      />
      <div className="flex flex-wrap justify-end gap-2">
        {row && (
          <ConfirmButton
            message={t('admin.catalogTranslations.deleteConfirm', { language: languageName(locale) })}
            onConfirm={() => del.mutateAsync(undefined)}
          >
            {t('common.delete')}
          </ConfirmButton>
        )}
        <Button
          variant="primary"
          loading={save.isPending}
          disabled={f.name.trim().length < 2}
          onClick={() => save.mutate(undefined)}
        >
          {t('admin.catalogTranslations.saveLanguage', { language: languageName(locale) })}
        </Button>
      </div>
    </div>
  );
}
